import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './TimeTracking.css';

function TimeTracking() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('clocks');
  const [employees, setEmployees] = useState([]);
  const [clocks, setClocks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    startingDate: new Date().toISOString().split('T')[0],
    roleId: '',
    ratePerHour: '',
  });
  const [roles, setRoles] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [jobDescription, setJobDescription] = useState('');

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch('/api/time-tracking/employees', getAuthHeader());
      const data = await res.json();
      if (data.success) setEmployees(data.employees);
    } catch (err) {
      console.error('Fetch employees error:', err);
    }
  }, [getAuthHeader]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await fetch('/api/time-tracking/roles', getAuthHeader());
      const data = await res.json();
      if (data.success) setRoles(data.roles);
    } catch (err) {
      console.error('Fetch roles error:', err);
    }
  }, [getAuthHeader]);

  const fetchClocks = useCallback(async () => {
    try {
      const res = await fetch('/api/time-tracking/clocks', getAuthHeader());
      const data = await res.json();
      if (data.success) setClocks(data.clocks);
    } catch (err) {
      console.error('Fetch clocks error:', err);
    }
  }, [getAuthHeader]);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await fetch('/api/time-tracking/logs', getAuthHeader());
      const data = await res.json();
      if (data.success) setLogs(data.logs);
    } catch (err) {
      console.error('Fetch logs error:', err);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchEmployees();
    fetchRoles();
    fetchClocks();
    fetchLogs();
  }, [fetchEmployees, fetchRoles, fetchClocks, fetchLogs]);

  const handleClockIn = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) {
      setError('Please select an employee');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch('/api/time-tracking/clocks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader().headers },
        body: JSON.stringify({ employeeId: selectedEmployee, jobDescription }),
      });
      const data = await res.json();

      if (data.success) {
        setSuccess('Clock started successfully');
        setSelectedEmployee('');
        setJobDescription('');
        fetchClocks();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to start clock');
      }
    } catch (err) {
      setError('Failed to start clock');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async (clockId) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`/api/time-tracking/clocks/${clockId}/clock-out`, {
        method: 'PUT',
        ...getAuthHeader(),
      });
      const data = await res.json();

      if (data.success) {
        setSuccess('Clock stopped successfully');
        fetchClocks();
        fetchLogs();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to stop clock');
      }
    } catch (err) {
      setError('Failed to stop clock');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!newEmployee.fullName || !newEmployee.phoneNumber || !newEmployee.startingDate || !newEmployee.roleId) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/time-tracking/employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader().headers },
        body: JSON.stringify(newEmployee),
      });
      const data = await res.json();

      if (data.success) {
        setSuccess('Employee added successfully');
        setShowEmployeeModal(false);
        setNewEmployee({
          fullName: '',
          phoneNumber: '',
          email: '',
          startingDate: new Date().toISOString().split('T')[0],
          roleId: '',
          ratePerHour: '',
        });
        fetchEmployees();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to add employee');
      }
    } catch (err) {
      setError('Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="tt-page">
      <div className="tt-side-handle">{'\u2039'}</div>

      <header className="tt-header">
        <div className="tt-title-row">
          <button className="tt-back" onClick={() => navigate('/')}>{'\u2190'}</button>
          <h1>Time Tracking</h1>
        </div>
        <button className="tt-refresh" type="button" onClick={() => { fetchClocks(); fetchLogs(); }}>
          Refresh <span aria-hidden="true">{'\u21BB'}</span>
        </button>
      </header>

      {error && <div className="tt-alert tt-alert-error">{error}</div>}
      {success && <div className="tt-alert tt-alert-success">{success}</div>}

      <div className="tt-tabs" role="tablist" aria-label="Time Tracking">
        <button
          className={`tt-tab ${activeTab === 'clocks' ? 'active' : ''}`}
          onClick={() => setActiveTab('clocks')}
          type="button"
        >
          Time Clocks {clocks.length > 0 && <span className="tt-badge">{clocks.length}</span>}
        </button>
        <button
          className={`tt-tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
          type="button"
        >
          Time Logs
        </button>
        <button
          className={`tt-tab ${activeTab === 'employees' ? 'active' : ''}`}
          onClick={() => setActiveTab('employees')}
          type="button"
        >
          Employees
        </button>
      </div>

      {activeTab === 'clocks' && (
        <div className="tt-content">
          <section className="tt-clock-form">
            <h3>Start a New Clock</h3>
            <form onSubmit={handleClockIn}>
              <div className="tt-form-row">
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="tt-select"
                >
                  <option value="">Select Employee...</option>
                  {employees.map((emp) => (
                    <option key={emp.EmployeeId} value={emp.EmployeeId}>
                      {emp.FullName}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Job description (optional)"
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  className="tt-input"
                />
                <button type="submit" className="tt-btn tt-btn-clock-in" disabled={loading}>
                  Clock In
                </button>
              </div>
            </form>
          </section>

          <section className="tt-active-clocks">
            <h3>Active Clocks ({clocks.length})</h3>
            {clocks.length === 0 ? (
              <p className="tt-empty">No active clocks</p>
            ) : (
              <div className="tt-clock-list">
                {clocks.map((clock) => (
                  <div key={clock.TimeClockId} className="tt-clock-card">
                    <div className="tt-clock-info">
                      <strong>{clock.EmployeeName}</strong>
                      <span className="tt-clock-time">
                        In: {formatTime(clock.ClockInTime)}
                      </span>
                      {clock.JobDescription && (
                        <span className="tt-clock-job">{clock.JobDescription}</span>
                      )}
                    </div>
                    <button
                      className="tt-btn tt-btn-clock-out"
                      onClick={() => handleClockOut(clock.TimeClockId)}
                      disabled={loading}
                    >
                      Clock Out
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="tt-content">
          <h3>Time History</h3>
          {logs.length === 0 ? (
            <p className="tt-empty">No time logs yet</p>
          ) : (
            <table className="tt-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>In</th>
                  <th>Out</th>
                  <th>Duration</th>
                  <th>Job</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.TimeLogId}>
                    <td>{log.EmployeeName}</td>
                    <td>{formatDate(log.Date)}</td>
                    <td>{formatTime(log.ClockInTime)}</td>
                    <td>{formatTime(log.ClockOutTime)}</td>
                    <td>{log.TotalHours ? `${log.TotalHours}h` : '-'}</td>
                    <td>{log.JobDescription || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'employees' && (
        <div className="tt-content">
          <div className="tt-section-header">
            <h3>Employees ({employees.length})</h3>
            <button
              className="tt-btn tt-btn-add"
              onClick={() => setShowEmployeeModal(true)}
            >
              + Add Employee
            </button>
          </div>
          {employees.length === 0 ? (
            <p className="tt-empty">No employees yet. Add one to start tracking time.</p>
          ) : (
            <div className="tt-employee-list">
              {employees.map((emp) => (
                <div key={emp.EmployeeId} className="tt-employee-card">
                  <strong>{emp.FullName}</strong>
                  <span>{emp.PhoneNumber}</span>
                  <span>{emp.RoleName}</span>
                  <span className={emp.IsActive ? 'tt-active' : 'tt-inactive'}>
                    {emp.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showEmployeeModal && (
        <div className="tt-modal-overlay" onClick={() => setShowEmployeeModal(false)}>
          <div className="tt-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Employee</h3>
            <form onSubmit={handleAddEmployee}>
              <div className="tt-form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={newEmployee.fullName}
                  onChange={(e) => setNewEmployee({ ...newEmployee, fullName: e.target.value })}
                  className="tt-input"
                  required
                />
              </div>
              <div className="tt-form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={newEmployee.phoneNumber}
                  onChange={(e) => setNewEmployee({ ...newEmployee, phoneNumber: e.target.value })}
                  className="tt-input"
                  required
                />
              </div>
              <div className="tt-form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                  className="tt-input"
                />
              </div>
              <div className="tt-form-group">
                <label>Starting Date *</label>
                <input
                  type="date"
                  value={newEmployee.startingDate}
                  onChange={(e) => setNewEmployee({ ...newEmployee, startingDate: e.target.value })}
                  className="tt-input"
                  required
                />
              </div>
              <div className="tt-form-group">
                <label>Role *</label>
                <select
                  value={newEmployee.roleId}
                  onChange={(e) => setNewEmployee({ ...newEmployee, roleId: e.target.value })}
                  className="tt-select"
                  required
                >
                  <option value="">Select Role...</option>
                  {roles.map((role) => (
                    <option key={role.RoleId} value={role.RoleId}>
                      {role.RoleName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tt-form-group">
                <label>Hourly Rate</label>
                <input
                  type="number"
                  step="0.01"
                  value={newEmployee.ratePerHour}
                  onChange={(e) => setNewEmployee({ ...newEmployee, ratePerHour: e.target.value })}
                  className="tt-input"
                />
              </div>
              <div className="tt-modal-actions">
                <button type="button" className="tt-btn tt-btn-cancel" onClick={() => setShowEmployeeModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="tt-btn tt-btn-submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimeTracking;
